import { ElectronAPI, LogLevel } from "@org-avp/avp-avengers-ui-framework-electron-api";

type LogFunc = (...args: unknown[]) => void;

export function initLogIntercept(electronApi: ElectronAPI) {
  const originalFunctions: Record<LogLevel, LogFunc> = {
    log: console.log,
    info: console.info,
    warn: console.warn,
    error: console.error,
    debug: console.debug,
  };

  (Object.entries(originalFunctions) as [LogLevel, LogFunc][]).forEach(([level, originalFunc]) => {
    console[level] = (...args: unknown[]) => {
      originalFunc(...args);
      electronApi.invoke.logMessage(level as LogLevel, ...args).then();
    };
  });

  console.info("Console interception for logging initialized");

  setupNetworkErrorForwarding();
}

function errorToMessage(error: unknown): string {
  if (error instanceof Error) {
    const cause = (error as Error & { cause?: unknown }).cause;
    const causeText = cause ? ` | cause: ${String(cause)}` : "";
    return `${error.name}: ${error.message}${causeText}`;
  }
  return String(error);
}

function setupNetworkErrorForwarding(): void {
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const method = init?.method ?? (input instanceof Request ? input.method : "GET");
    const url = input instanceof Request ? input.url : String(input);
    try {
      return await originalFetch(input, init);
    } catch (error) {
      console.error(`[network] ${method.toUpperCase()} ${url} - ${errorToMessage(error)}`);
      throw error;
    }
  };

  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function patchedOpen(method: string, url: string | URL, ...rest: unknown[]): void {
    (this as XMLHttpRequest & { __avengersMethod?: string }).__avengersMethod = method;
    (this as XMLHttpRequest & { __avengersUrl?: string }).__avengersUrl = String(url);
    originalOpen.call(this, method, url, rest[0] as boolean ?? true, rest[1] as string | undefined, rest[2] as string | undefined);
  };

  XMLHttpRequest.prototype.send = function patchedSend(...args: unknown[]): void {
    this.addEventListener("error", () => {
      const request = this as XMLHttpRequest & { __avengersMethod?: string; __avengersUrl?: string };
      const method = (request.__avengersMethod ?? "GET").toUpperCase();
      const url = request.__avengersUrl ?? "unknown-url";
      console.error(`[network] ${method} ${url} - XHR network error`);
    }, { once: true });

    this.addEventListener("abort", () => {
      const request = this as XMLHttpRequest & { __avengersMethod?: string; __avengersUrl?: string };
      const method = (request.__avengersMethod ?? "GET").toUpperCase();
      const url = request.__avengersUrl ?? "unknown-url";
      console.warn(`[network] ${method} ${url} - XHR request aborted`);
    }, { once: true });

    originalSend.call(this, args[0] as Document | XMLHttpRequestBodyInit | null | undefined);
  };

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const maybeMessage = typeof reason === "object" && reason !== null && "message" in reason
      ? String((reason as { message: unknown }).message)
      : String(reason);
    if (maybeMessage.toLowerCase().includes("failed to fetch") || maybeMessage.toLowerCase().includes("network")) {
      console.error(`[network] unhandled rejection - ${errorToMessage(reason)}`);
    }
  });

  console.log("Network error forwarding to main process initialized");
}
